<template>
  <div class="content">
    <div class="basemap-header">
      <ys-text type="title4">Basemap</ys-text>
      <ys-button
        variant="secondary"
        width="inline"
        size="small"
        class="map-legend-button"
        data-test="legend-btn"
        @click="$emit('open-legend')"
      >
        <ys-icon slot="icon" icon="ysLegend" size="small" class="legend-icon" />
        Map Legend
      </ys-button>
    </div>
    <section>
      <basemaps-container class="container" />
    </section>
    <section>
      <ys-text type="title4">{{ modeLabel }} Layers</ys-text>
      <div class="container">
        <ul>
          <template v-for="(layer, index) in modeLayers" :key="layer.id">
            <li data-test="mode-layer">
              <template v-if="showLayer(layer)">
                <layer-toggle
                  :layer="layer"
                  :data-test="`${layer.id}-mode-toggle`"
                  @open-layer-details="$emit('open-layer-details', layer)"
                />
                <hr />
              </template>
            </li>
            <!-- Insert wind-toggle after second item -->
            <li v-if="index === 1" key="wind" data-test="mode-layer">
              <wind-layer-toggle
                data-test="wind-mode-toggle"
                @open-layer-details="
                  $emit('open-layer-details', WIND_LAYER_INFO)
                "
              />
              <hr v-if="modeLayers.length > 1" />
            </li>
          </template>
        </ul>
      </div>
    </section>
  </div>
</template>
<script lang="ts">
export default {
  name: 'ControlLayers',
}
</script>
<script setup lang="ts">
///// imports /////
import { computed } from 'vue'
import '@onxmaps/yellowstone-ui/ys-button'
import '@onxmaps/yellowstone-ui/ys-text'
import '@onxmaps/yellowstone-ui/ys-icon'
import '@onxmaps/yellowstone-ui/icons/ysLegend'

import layerToggle from '@/components/map-controls/control-layers/layer-toggle.vue'
import windLayerToggle from '@/fish/components/map-controls/wind-layer-toggle.vue'
import { WIND_LAYER_INFO } from '@/fish/components/map-controls/utils'

import { isActive } from '@/services/feature-flag-service'
import layerService from '@/services/layer-service'
import { getMode } from '@/lib/modes'
import {
  getLabelForMode,
  getModeToggleableLayers,
} from '@/components/map-controls/control-layers/utils'
import basemapsContainer from '@/fish/components/map-controls/basemap/basemap-container.vue'

///// props/emits /////
defineEmits(['close-card', 'open-layer-details', 'open-legend'])

///// refs and variables /////
const app = 'fish'

///// computed /////
const mode = computed(getMode)
const modeLabel = computed(() => getLabelForMode(mode.value))

// FUTURE - update TS here to use interface from activity modes/layer service when one is defined
const modeLayers = computed(() =>
  getModeToggleableLayers(mode.value, app).map((layer: any) => ({
    ...layer,
    ...layerService.getLayer(layer.id),
  }))
)

///// functions /////
// FUTURE - update TS here to use interface from activity modes/layer service when one is defined
const showLayer = (layer: any) => {
  if (!layer?.layerGateKey) {
    return true
  }
  return isActive(layer.layerGateKey)
}
</script>
<style scoped>
.content {
  padding: 0px var(--ys-spacing-200) var(--ys-spacing-400);
  background-color: var(--ys-color-background-primary);
}

.container {
  background-color: var(--ys-color-background-primary);
  border-radius: 8px;
  margin: 8px 0px var(--ys-spacing-400);
  padding: var(--ys-spacing-200) 0;

  hr {
    margin: var(--ys-spacing-200) calc(-1 * var(--ys-spacing-200));
  }
}

.basemap-header {
  display: flex;
  align-items: end;
  justify-content: space-between;
}

.map-legend-button {
  margin-bottom: 0;

  .legend-icon {
    margin-right: var(--ys-spacing-100);
  }
}
</style>
